import { useCallback, useEffect, useState } from 'react';
import { dismissActivation, getActivation, setOffering } from '../../api/onboarding';
import type { Activation, ActivationCommercial, BusinessOffering } from '../../types/onboarding';
import { activationCardView, activationChecklistView, type ChecklistItemView } from '../../utils/activationView';
import { noteOnboardingError } from '../../utils/onboardingTelemetry';
import { useRequestGuard } from '../../utils/useRequestGuard';
import { QuickProductForm } from '../products/QuickProductForm';
import { Icon } from '../ui/Icon';
import { Button, Card, Chip, Dialog } from '../ui/primitives';
import { CardHead, Tile } from './InicioParts';

const TITLE_ID = 'inicio-activation-title';

const OFFERINGS: ReadonlyArray<{ value: BusinessOffering; label: string }> = [
  { value: 'PRODUCTS', label: 'Productos' },
  { value: 'SERVICES', label: 'Servicios' },
  { value: 'BOTH', label: 'Productos y servicios' },
];

/**
 * ONB-001 (Macrofase A) — la única pieza que el onboarding agrega al Inicio del Bloque C: una tarjeta
 * contextual y temporal, en su propia fila entre el hero y «Así va tu negocio». El resto del Inicio no
 * cambia.
 *
 * Solo el dueño de un negocio que nació con el registro guiado la ve, y dice lo que el SERVIDOR sabe: el
 * siguiente paso real antes de la primera venta (qué vende, primer artículo, primera venta) y, después,
 * pocas recomendaciones según lo que vende. Sin porcentaje y sin «100 %».
 *
 * P-085: el checklist en tres niveles. Antes de la primera venta, debajo del siguiente paso, «Lo indispensable» con sus
 * marcas; después, «Recomendado» y «Opcional» (Reportes es un acceso, sin marca). La condición comercial (beta sin
 * costo o días de prueba) sale hacia el encabezado del Inicio con `onCommercial`: no vive en esta tarjeta, que se puede
 * ocultar (Codex R1, K8).
 *
 * Es guía, no un dato del negocio: mientras carga no ocupa lugar (un negocio anterior nunca ve aparecer
 * y desaparecer un marcador) y, si la llamada falla, no se dibuja; el Inicio sigue completo.
 */
export function InicioActivation({ onNavigate, onCommercial }: {
  onNavigate: (destination: string) => void;
  /** P-085: la condición comercial que dijo el servidor (o null). */
  onCommercial?: (commercial: ActivationCommercial) => void;
}) {
  const [activation, setActivation] = useState<Activation | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string>();
  const request = useRequestGuard();

  const load = useCallback(() => {
    const isCurrent = request();
    getActivation()
      .then((result) => { if (isCurrent()) setActivation(result); })
      .catch(() => { if (isCurrent()) setActivation(null); });
  }, [request]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { onCommercial?.(activation?.commercial ?? null); }, [activation, onCommercial]);

  const act = async (key: string, action: () => Promise<Activation>, failure: string) => {
    if (busy) return;
    setBusy(key); setError(undefined);
    try { setActivation(await action()); }
    catch (err) {
      setError(failure);
      if (key !== 'dismiss') noteOnboardingError('offering', err);
    }
    finally { setBusy(null); }
  };

  const view = activationCardView(activation);
  const checklist = activationChecklistView(activation);
  // P-085: después de la primera venta, el checklist sigue mientras le falte algo (aunque las cuatro recomendaciones de
  // siempre ya estén hechas, puede faltar «costos»), salvo que la persona lo haya ocultado.
  const postAhaChecklist = !!activation?.hasCompletedSale && !activation.dismissed && !!checklist && checklist.pendingAfterAha > 0;
  if (view.kind === 'none' && !postAhaChecklist) return null;

  return (
    <div className="n3-inicio__band">
      <div className="n3-inicio__cell n3-inicio__cell--main">
        <Card aria-labelledby={TITLE_ID} as="section" className="n3-inicio__activation">
          {view.kind === 'offering' && (
            <>
              <CardHead icon={<Icon name="bulb" size={20} />} id={TITLE_ID} title="¿Qué vas a vender principalmente?" />
              <p className="n3-inicio__support">Así te sugerimos solo lo que te sirve.</p>
              <div aria-label="Qué vas a vender" className="n3-inicio__activation-choices" role="group">
                {OFFERINGS.map((option) => (
                  <Chip
                    className="n3-inicio__activation-choice"
                    disabled={busy !== null}
                    key={option.value}
                    onClick={() => void act(option.value, () => setOffering(option.value), 'No pudimos guardar tu respuesta. Inténtalo de nuevo.')}
                    selected={busy === option.value}
                  >
                    {option.label}
                  </Chip>
                ))}
              </div>
            </>
          )}

          {view.kind === 'first-item' && (
            <>
              <CardHead icon={<Icon name="bulb" size={20} />} id={TITLE_ID} title={`Crea tu primer ${view.noun}`} />
              <p className="n3-inicio__support">Solo el nombre y el precio. Después lo cobras en el punto de venta.</p>
              <div className="n3-inicio__activation-actions">
                <Button iconStart={<Icon name="plus" size={18} />} onClick={() => setCreating(true)} variant="primary">{`Crear ${view.noun}`}</Button>
              </div>
            </>
          )}

          {view.kind === 'first-sale' && (
            <>
              <CardHead icon={<Icon name="bulb" size={20} />} id={TITLE_ID} title="Haz tu primera venta" />
              <p className="n3-inicio__support">Ya tienes algo que vender. Abre el punto de venta y cobra tu primera venta real.</p>
              <div className="n3-inicio__activation-actions">
                <Button iconStart={<Icon name="sell" size={18} />} onClick={() => onNavigate('vender')} variant="primary">Abrir punto de venta</Button>
              </div>
            </>
          )}

          {/* P-085: lo indispensable, con sus marcas, debajo del siguiente paso (antes de la primera venta). */}
          {checklist && view.kind !== 'progress' && view.kind !== 'none' && (
            <ChecklistGroup items={checklist.essentials} label="Lo indispensable" onNavigate={onNavigate} />
          )}

          {(view.kind === 'progress' || postAhaChecklist) && (
            <>
              <CardHead icon={<Icon name="bulb" size={20} />} id={TITLE_ID} title="Sácale más provecho a Negocio360" />
              <p className="n3-inicio__support">Tu negocio ya está vendiendo. Cuando quieras, esto te ayuda a llevarlo mejor:</p>
              {checklist ? (
                <>
                  {checklist.recommended.length > 0 && <ChecklistGroup items={checklist.recommended} label="Recomendado" onNavigate={onNavigate} />}
                  {checklist.optional.length > 0 && <ChecklistGroup items={checklist.optional} label="Opcional" onNavigate={onNavigate} />}
                </>
              ) : view.kind === 'progress' && (
                <ul className="n3-inicio__activation-list">
                  {view.items.map((item) => (
                    <li className="n3-inicio__activation-item" key={item.key}>
                      <Tile icon={item.icon} size={19} tone="brand" />
                      <span className="n3-inicio__activation-text">
                        <strong className="n3-inicio__activation-title">{item.title}</strong>
                        <span className="n3-inicio__activation-detail">{item.text}</span>
                      </span>
                      <Button className="n3-inicio__activation-go" onClick={() => onNavigate(item.destination)} variant="tonal">{item.actionLabel}</Button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="n3-inicio__activation-foot">
                <Button className="n3-inicio__link" loading={busy === 'dismiss'} loadingLabel="Ocultando…" onClick={() => void act('dismiss', dismissActivation, 'No pudimos ocultar estas sugerencias. Inténtalo de nuevo.')} variant="ghost">
                  Ocultar sugerencias
                </Button>
              </div>
            </>
          )}

          {error && <p className="n3-inicio__activation-error" role="alert"><Icon name="warning" size={16} /><span>{error}</span></p>}
        </Card>
      </div>

      {creating && view.kind === 'first-item' && (
        <Dialog description="Solo el nombre y el precio." onClose={() => setCreating(false)} title={`Nuevo ${view.noun}`}>
          <QuickProductForm
            offering={activation?.offering ?? null}
            // Codex R2-04: igual que en la guía (POS directo tras el primer artículo): de aquí, al punto de venta.
            onCreated={() => { setCreating(false); onNavigate('vender'); }}
            secondaryAction={<Button fullWidth onClick={() => setCreating(false)} variant="ghost">Cancelar</Button>}
          />
        </Dialog>
      )}
    </div>
  );
}

/** P-085: un nivel del checklist. Lo hecho lleva su marca; lo pendiente, su acceso; un acceso sin marca, solo su botón. */
function ChecklistGroup({ label, items, onNavigate }: { label: string; items: ChecklistItemView[]; onNavigate: (destination: string) => void }) {
  return (
    <section aria-label={label} className="n3-inicio__checklist">
      <h3 className="n3-inicio__checklist-title">{label}</h3>
      <ul className="n3-inicio__checklist-list">
        {items.map((item) => (
          <li className="n3-inicio__checklist-item" key={item.key}>
            <span aria-hidden="true" className={item.done ? 'n3-inicio__checklist-mark n3-inicio__checklist-mark--done' : 'n3-inicio__checklist-mark'}>
              <Icon name={item.done ? 'check' : item.icon} size={14} />
            </span>
            <span className="n3-inicio__checklist-text">
              <strong className="n3-inicio__checklist-name">{item.title}</strong>
              {item.text && <span className="n3-inicio__checklist-detail">{item.text}</span>}
              {item.done !== null && <span className="n3-visually-hidden">{item.done ? 'Hecho' : 'Pendiente'}</span>}
            </span>
            {item.done !== true && item.destination && item.actionLabel && (
              <Button className="n3-inicio__checklist-go" onClick={() => onNavigate(item.destination!)} variant="tonal">{item.actionLabel}</Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
